import type { PartDef } from "./types";

/** All values are heavily simplified for a 9–12 audience but follow the spirit of real rocketry. */
export const PARTS: PartDef[] = [
  // ----------------- Command -----------------
  {
    id: "pod-mk1",
    name: "Mk1 Capsule",
    category: "command",
    icon: "🧑‍🚀",
    mass: 840,
    halfWidth: 0.62,
    height: 1.2,
    isControl: true,
    maxTemp: 2400,
    drag: 0.4,
    nodes: [{ id: "top", x: 0, y: 1.2, kind: "stack" }, { id: "bottom", x: 0, y: 0, kind: "stack" }],
    render: { shape: "capsule", color: "#b9c4d2" },
  },
  {
    id: "probe-hex",
    name: "Hex Probe Core",
    category: "command",
    icon: "🛰️",
    mass: 60,
    halfWidth: 0.3,
    height: 0.2,
    isControl: true,
    maxTemp: 1400,
    nodes: [{ id: "top", x: 0, y: 0.2, kind: "stack" }, { id: "bottom", x: 0, y: 0, kind: "stack" }],
    render: { shape: "box", color: "#8d97a8" },
  },

  // ----------------- Fuel -----------------
  {
    id: "tank-lfo-s",
    name: "Liquid Tank (S)",
    category: "fuel",
    icon: "⛽",
    mass: 180,
    halfWidth: 0.6,
    height: 1.6,
    fuelKind: "liquid",
    fuelCapacity: 900,
    nodes: [{ id: "top", x: 0, y: 1.6, kind: "stack" }, { id: "bottom", x: 0, y: 0, kind: "stack" }],
    render: { shape: "cylinder", color: "#e8eaef" },
  },
  {
    id: "tank-lfo-m",
    name: "Liquid Tank (M)",
    category: "fuel",
    icon: "⛽",
    mass: 360,
    halfWidth: 0.6,
    height: 3.2,
    fuelKind: "liquid",
    fuelCapacity: 1800,
    nodes: [{ id: "top", x: 0, y: 3.2, kind: "stack" }, { id: "bottom", x: 0, y: 0, kind: "stack" }],
    render: { shape: "cylinder", color: "#e8eaef" },
  },
  {
    id: "tank-lfo-l",
    name: "Liquid Tank (L)",
    category: "fuel",
    icon: "⛽",
    mass: 720,
    halfWidth: 0.6,
    height: 6.4,
    fuelKind: "liquid",
    fuelCapacity: 3600,
    nodes: [{ id: "top", x: 0, y: 6.4, kind: "stack" }, { id: "bottom", x: 0, y: 0, kind: "stack" }],
    render: { shape: "cylinder", color: "#e8eaef" },
  },

  // ----------------- Propulsion -----------------
  {
    id: "engine-lfo",
    name: "Atlas Liquid Engine",
    category: "propulsion",
    icon: "🔥",
    mass: 160,
    halfWidth: 0.6,
    height: 0.9,
    thrust: 215_000,
    ispVac: 320,
    ispAsl: 280,
    gimbal: 5,
    throttleable: true,
    maxTemp: 2200,
    nodes: [{ id: "top", x: 0, y: 0.9, kind: "stack" }],
    render: { shape: "cone", color: "#8a8f98" },
  },
  {
    id: "engine-lfo-big",
    name: "Hydra Vacuum Engine",
    category: "propulsion",
    icon: "🔥",
    mass: 380,
    halfWidth: 0.62,
    height: 1.4,
    thrust: 680_000,
    ispVac: 355,
    ispAsl: 220,
    gimbal: 4,
    throttleable: true,
    maxTemp: 2200,
    nodes: [{ id: "top", x: 0, y: 1.4, kind: "stack" }],
    render: { shape: "cone", color: "#55606d" },
  },
  {
    id: "booster-srb",
    name: "Solid Booster",
    category: "propulsion",
    icon: "🧨",
    mass: 750,
    halfWidth: 0.5,
    height: 5.0,
    thrust: 250_000,
    ispVac: 240,
    ispAsl: 210,
    gimbal: 0,
    throttleable: false,
    fuelKind: "solid",
    fuelCapacity: 2300,
    maxTemp: 2000,
    drag: 0.3,
    nodes: [
      { id: "top", x: 0, y: 5.0, kind: "stack" },
      { id: "bottom", x: 0, y: 0, kind: "stack" },
      { id: "side", x: 0, y: 2.5, kind: "surface" },
    ],
    render: { shape: "cylinder", color: "#d7b584" },
  },

  // ----------------- Aero -----------------
  {
    id: "parachute",
    name: "Parachute",
    category: "aero",
    icon: "🪂",
    mass: 40,
    halfWidth: 0.45,
    height: 0.4,
    chuteDragMult: 22,
    chuteDeployAltitude: 900,
    drag: 0.1,
    maxTemp: 900,
    nodes: [{ id: "bottom", x: 0, y: 0, kind: "stack" }],
    render: { shape: "dome", color: "#d2594a" },
  },
  {
    id: "fairing",
    name: "Payload Fairing",
    category: "aero",
    icon: "🔻",
    mass: 120,
    halfWidth: 0.65,
    height: 1.8,
    drag: 0.2,
    maxTemp: 1800,
    nodes: [{ id: "bottom", x: 0, y: 0, kind: "stack" }, { id: "top", x: 0, y: 1.8, kind: "stack" }],
    render: { shape: "cone", color: "#f4f6fa" },
  },
  {
    id: "fins",
    name: "Control Fins",
    category: "aero",
    icon: "🐟",
    mass: 30,
    halfWidth: 0.25,
    height: 0.6,
    finLift: 0.3,
    drag: 0.05,
    maxTemp: 1500,
    nodes: [{ id: "mount", x: 0, y: 0.3, kind: "surface" }],
    render: { shape: "fin", color: "#3b4454" },
  },

  // ----------------- Utility -----------------
  {
    id: "decoupler",
    name: "Stack Decoupler",
    category: "structural",
    icon: "🔗",
    mass: 50,
    halfWidth: 0.6,
    height: 0.3,
    isDecoupler: true,
    nodes: [
      { id: "top", x: 0, y: 0.3, kind: "stack" },
      { id: "bottom", x: 0, y: 0, kind: "stack" },
    ],
    render: { shape: "cylinder", color: "#545b67" },
  },
  {
    id: "legs",
    name: "Landing Legs",
    category: "utility",
    icon: "🦵",
    mass: 60,
    halfWidth: 0.2,
    height: 1.0,
    landingLegSpan: 2.0,
    nodes: [{ id: "mount", x: 0, y: 0.5, kind: "surface" }],
    render: { shape: "leg", color: "#6c7583" },
  },
  {
    id: "solar",
    name: "Solar Panel",
    category: "utility",
    icon: "☀️",
    mass: 30,
    halfWidth: 0.8,
    height: 0.1,
    power: 400,
    nodes: [{ id: "mount", x: 0, y: 0.05, kind: "surface" }],
    render: { shape: "dish", color: "#203a6b" },
  },
];

export const PART_BY_ID = new Map(PARTS.map((p) => [p.id, p]));

export function partById(id: string): PartDef {
  const p = PART_BY_ID.get(id);
  if (!p) throw new Error(`Unknown part: ${id}`);
  return p;
}
